import React, { useState, useCallback, useEffect } from 'react'

import PropTypes from 'prop-types'
import { Box, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { matchPath } from 'react-router-dom'

import {
  DialogUseLimitationNotice,
  NoAccessFreeTrial,
  PromotionalBanner,
} from 'components'
import DaiChatPanel from '../DaiChatPanel'
import FloatingButtons from '../FloatingButtons'
import NavBar from '../NavBar'
import TopBar from '../TopBar'

import DaiChatThemeProvider from 'providers/DaiChatThemeProvider'

import useCustomTheme from 'hooks/useCustomTheme'
import useDaiChat from 'hooks/useDaiChat'
import useDrawer from 'hooks/useDrawer'
import useFetch from 'hooks/useFetch'

import * as service from 'service'
import constants from 'constants/index'
import styles from './styles'
import { routes } from 'Routes'

const useStyles = makeStyles(styles)

const ContentMain = ({
  children,
  showBackToFirstSteps,
  reduceMargin,
  company,
  handleOpenTomticketDialog,
  dialogTomticket,
  setDialogTomticket,
}) => {
  const { DPO_DAY_BANNER_HIDDEN_VARIANT } = constants.dpoday
  const classes = useStyles()
  const { customThemeIsActive } = useCustomTheme()
  const { chatOpen } = useDaiChat()
  const isDocumentCreationOrEditPage = !!matchPath(window.location.pathname, {
    path: [routes.documents.new, routes.documents.edit],
    exact: true,
  })

  const { handleUnfixDrawer, handleFixDrawer } = useDrawer()
  const [bannerVisible, setBannerVisible] = useState(!customThemeIsActive)

  const {
    response: promotionalBannerResponse,
    error: promotionalBannerError,
    isLoadingStart: promotionalBannerLoading,
  } = useFetch(
    service.dponet.promotionalBanners.get,
    null,
    [],
    !customThemeIsActive,
  )

  const promotionalBannerVariant = promotionalBannerResponse?.data?.variant
  const isDpoDayCampaignActive =
    !customThemeIsActive &&
    !promotionalBannerLoading &&
    !promotionalBannerError &&
    !!promotionalBannerVariant &&
    promotionalBannerVariant !== DPO_DAY_BANNER_HIDDEN_VARIANT

  const handleBannerVisibilityChange = useCallback((visible) => {
    setBannerVisible(visible)
  }, [])

  useEffect(() => {
    if (chatOpen || isDocumentCreationOrEditPage) {
      handleUnfixDrawer()
    } else {
      handleFixDrawer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen, isDocumentCreationOrEditPage])

  return (
    <Box className={classes.mainWrapper}>
      {isDpoDayCampaignActive && (
        <>
          <PromotionalBanner.DPODayBanner
            variant={promotionalBannerVariant}
            daysRemaining={promotionalBannerResponse?.data?.daysRemaining}
            onVisibilityChange={handleBannerVisibilityChange}
          />
          <PromotionalBanner.DpoDayFloatingButton
            bannerVisible={bannerVisible}
            chatOpen={chatOpen}
          />
        </>
      )}
      <Box
        className={clsx(classes.root, {
          [classes.standardBackground]: !customThemeIsActive,
          [classes.customBackground]: customThemeIsActive,
        })}
      >
        <TopBar />
        <Box className={classes.navbar}>
          <NavBar />
        </Box>

        <DaiChatThemeProvider>
          <Box className={classes.wrapper}>
            <Box
              className={clsx(classes.contentContainer, {
                [classes.contentContainerMarginReduce]: reduceMargin,
              })}
            >
              <Box className={classes.content}>
                {children}

                <NoAccessFreeTrial />
                <FloatingButtons
                  company={company}
                  showBackToFirstSteps={showBackToFirstSteps}
                  handleOpenTomticketDialog={handleOpenTomticketDialog}
                  dialogTomticket={dialogTomticket}
                  setDialogTomticket={setDialogTomticket}
                />
                <DialogUseLimitationNotice />
              </Box>
            </Box>
          </Box>
        </DaiChatThemeProvider>

        <DaiChatPanel bannerVisible={isDpoDayCampaignActive && bannerVisible} />
      </Box>
    </Box>
  )
}

export default ContentMain

ContentMain.propTypes = {
  children: PropTypes.any,
  showBackToFirstSteps: PropTypes.bool,
  reduceMargin: PropTypes.bool,
  company: PropTypes.object,
  handleOpenTomticketDialog: PropTypes.func,
  dialogTomticket: PropTypes.bool,
  setDialogTomticket: PropTypes.bool,
}

ContentMain.defaultProps = {
  showBackToFirstSteps: false,
  reduceMargin: false,
}
